import type * as zod from 'zod';

import { FailureKind } from '@sb/routine-runner/FailureKind';

import type { TaggedRoutineStepVariables } from '../RoutineStepVariables';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class SetEnvironmentVariableStep extends Step<
  Arguments,
  Variables
> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const value = await this.routineContext.evaluateExpression(
      this.args.newValue,
    );

    if (await super.shouldStopPlaying()) {
      return;
    }

    let varState: TaggedRoutineStepVariables;

    try {
      varState = this.routineContext.getVariableState(this.args.variableID);
    } catch (error) {
      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `Unknown variable id ${this.args.variableID}`,
        error,
      });

      return;
    }

    if (varState.stepKind !== 'EnvironmentVariable') {
      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `Variable ID ${this.args.variableID} is not an environment variable`,
      });

      return;
    }

    /* NOTE: The `variableID` can be utilized as the `stepId` for the "Set Variable" step,
    since this step does not include any substeps. */
    this.routineContext.setVariableState(this.args.variableID, {
      ...varState,
      value,
    });
  }
}
